import axios, {
  AxiosInstance,
  AxiosResponse,
  Method,
  AxiosPromise,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios';
import { QuenchErrorResponse, Request } from '@/lib/types';
import { NavigateFunction } from 'react-router-dom';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import useUserStore from '@/store/useUserStore';
import { paths } from '@/routes/paths';
import useProfilesStore from '@/store/useProfilesStore';
import * as Sentry from '@sentry/react';

type AxiosCustomRequestHeaders = AxiosRequestHeaders & { Authorization: string };

type DecodedToken = {
  email: string;
  family_name: string;
  given_name: string;
  picture?: string;
};

interface CustomJwtPayload {
  email: string;
  family_name: string;
  given_name: string;
  exp?: number;
  picture?: string;
}

export class HttpClient {
  private httpClient: AxiosInstance;
  private isResponseInterceptorSetup: boolean = false;
  private token = '';

  constructor() {
    this.httpClient = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
      timeout: 60000,
      headers: {},
    });

    this.httpClient.interceptors.request.use(this.handleRequestUse.bind(this));
    this.httpClient.interceptors.response.use(this.handleResponseUse.bind(this));
  }

  private logOut() {
    useUserStore.getState().setToken('');
    useUserStore.getState().setUser(null);
    useProfilesStore.getState().resetProfiles();

    this.token = '';

    window.location.href = paths.LOGIN;
  }

  private isTokenExpired() {
    const token = useUserStore.getState().token || this.token;
    if (!token) {
      return true;
    }

    const decodedToken: JwtPayload = jwtDecode<JwtPayload>(token);
    const currentTime = Date.now() / 1000;

    if (decodedToken.exp) return decodedToken.exp < currentTime;

    return false;
  }

  private handleRequestUse(config: InternalAxiosRequestConfig) {
    const token = useUserStore.getState().token;
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `${token}`,
      } as unknown as AxiosCustomRequestHeaders;
    }
    return config;
  }

  private handleResponseUse(response: AxiosResponse) {
    // handle response interceptor logic here
    return response;
  }

  private async handleRequest<T>(
    url: string,
    method: Method,
    config: Request = {},
  ): Promise<AxiosResponse<T>> {
    if (this.isTokenExpired()) {
      this.logOut();
    }

    const { data, params, responseType, timeout } = config;

    const headers = {
      'Content-Type': 'application/json',
      ...config.headers,
    };

    return this.httpClient.request({
      url,
      method,
      data,
      params,
      headers,
      responseType,
      timeout,
    });
  }

  public setupResponseInterceptor(navigate: NavigateFunction) {
    if (this.isResponseInterceptorSetup) {
      return;
    }

    this.httpClient.interceptors.response.use(
      (response: AxiosResponse) => {
        return this.handleResponseUse(response);
      },
      (error: AxiosError<QuenchErrorResponse>) => {
        if (error.request?.status === 401 || error.response?.status === 403) {
          this.logOut();
        } else {
          Sentry.captureException(error);

          if (error.response?.data.userNotFound) {
            navigate(paths.USER_NOT_FOUND);
            return Promise.reject(error);
          }

          return Promise.reject(error);
        }
      },
    );

    this.isResponseInterceptorSetup = true;
  }

  public post<T>(url: string, config: Request = {}): AxiosPromise<T> {
    return this.handleRequest(url, 'post', config);
  }

  public get<T>(url: string, config: Request = {}): AxiosPromise<T> {
    return this.handleRequest(url, 'get', config);
  }

  public setAuthToken(token: string) {
    this.token = token;
    useUserStore.getState().setToken(token);
    const decodedToken: DecodedToken = jwtDecode<CustomJwtPayload>(token);
    const { email, family_name: lastName, given_name: firstName, picture } = decodedToken;
    useUserStore.getState().setUser({ email, firstName, lastName, picture });
    return token;
  }

  public logout() {
    this.logOut();
  }
}

export default new HttpClient();
